// --- React · 라우팅 · 다국어 ---
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import { getDeviceDisplayName } from '@packages/types/app';

// --- 전역 UI 스토어 (로딩, 알림, 모달, 선택) ---
import { useAppStore } from '../../../store/loadingStore';
import { useNotificationStore } from '../../../store/notificationStore';
import { useSelectionStore } from '@/store/selectionStore';
import { useModalStore } from '@/store/modalStore';
import { useAuthStore } from '@/store/authStore';

// --- 공통 UI 컴포넌트 ---
import { useTheme } from '@/theme';
import {
  Button,
  Card,
  Flex,
  Spinner,
  Text,
  SummaryCard,
  CardTitle,
  Grid,
  IconButton,
  InfoRow,
} from '@/components/ui';
import { DefaultContents } from '@/components/ui/common/DefaultContents';
import { formatPhoneForDisplay } from '@/components/ui/common/PhoneInput';

// --- 회사/사이트 스코프 훅 ---
import { useCompanySiteScope } from '@/hooks/useCompanySiteScope';
import { useMobileNavLayout } from '@/hooks/useMobileNavLayout';

// --- API 훅: 사이트 · 기기 ---
import { useGetSiteInfo } from '@/api/site/useSite';
import {
  useRegisterDevice,
  useGetSiteDevices,
  useDeleteDevice,
  mapDeviceJson,
  checkDeviceOwnership,
  type MappedDeviceInfo,
} from '@/api/device';

// --- 타입 정의 ---
import type { DeviceInfo, DeviceConfiguration } from '@packages/types/device/lite';
import type { Device } from '@packages/types/device/dto';
import type { SiteManager } from '@/api/company/useSiteManagers';

// --- i18n · 에러 파싱 · 날짜 ---
import { i18nKeys } from '@/i18n/i18nKeyGen';
import { parseSiteApiError } from '@/utils/errors';
import { formatDate, formatDueDateLine } from '@/utils/date';

// --- 페이지 전용 모달 · 컴포넌트 ---
import DeviceEditModal from './DeviceEditModal';
import { SiteCrudModal } from '../company/SiteCrudModal';
import SiteManagerCrudModal from '../company/SiteManagerCrudModal';
import CompanyManagerEditModal from '../company/CompanyManagerEditModal';
import { DataTable } from '@/components/common/DataTable';
import {
  createDeviceTableColumns,
  DeviceExpandedRow,
  type DeviceTableRow,
} from './DeviceTableColumns';

// --- 기기 연결 (BLE/시리얼) ---
import { ConnectDevice } from '@/components/device/connectDevice';
import { useConnectionStore } from '@/store/connectionStore';
import { protocolToUiConnectionType, uiConnectionTypeToLoadingProtocol } from '@/types/connection';
import { useConnectionActions } from '@/connections/hooks/useConnectionActions';

/**
 * Sites 페이지 컴포넌트
 *
 * 기능:
 * - 회사/사이트 선택
 * - 기기 등록 (크래들/블루투스)
 * - 등록된 기기 목록 조회
 */
const Sites: React.FC = () => {
  const { t } = useTranslation('site');
  const navigate = useNavigate();
  const { theme } = useTheme();
  // 모바일(<1024px)에서는 등록 카드 + 기기 테이블을 가로 배치 대신 세로로 쌓는다.
  const isMobile = useMobileNavLayout();
  const { showLoading, hideLoading, updateConnectedDevice } = useAppStore();
  const { showSuccess, showError, showInfo } = useNotificationStore();
  const { showConfirm, showAlert } = useModalStore();
  const { isAuthenticated, user: currentUser } = useAuthStore();
  const { protocol, device } = useConnectionStore();
  const updateDeviceCloud = useConnectionStore(state => state.updateDeviceCloud);
  const connectionType = protocolToUiConnectionType(protocol);

  // ==================== 상태 관리 ====================

  // 전역 상태 사용 (TopBar와 동기화)
  const { selectedCompanyId, selectedSiteId, setSelectedCompanyId, setSelectedSiteId } =
    useSelectionStore();

  // 기기 등록 상태
  const [registering, setRegistering] = useState(false);
  const [curDevice, setCurDevice] = useState<DeviceInfo | null>(null);
  const [curDeviceConfig, setCurDeviceConfig] = useState<DeviceConfiguration | null>(null);
  const [mappedDevice, setMappedDevice] = useState<MappedDeviceInfo | null>(null);

  // 사이트 편집 상태
  const [showSiteEditModal, setShowSiteEditModal] = useState(false);

  // 본인 프로필 편집 상태 (SiteManager)
  const [showProfileModal, setShowProfileModal] = useState(false);

  // Site Manager 편집 상태 (Admin / CompanyManager)
  const [showSiteManagerEditModal, setShowSiteManagerEditModal] = useState(false);
  const [editingSiteManager, setEditingSiteManager] = useState<SiteManager | null>(null);

  // 기기 편집 상태
  const [editingDevice, setEditingDevice] = useState<DeviceTableRow | null>(null);

  // 가이드 진행 중 여부 (기기가 없을 때 수정/삭제/상세 step 타겟을 위한 샘플 행 노출에 사용)
  const [isGuideActive, setIsGuideActive] = useState(false);

  // 기존 등록된 기기 정보 상태
  const [existingDeviceInfo, setExistingDeviceInfo] = useState<Device | null>(null);

  // 기기 정보 요청 플래그 (중복 요청 방지)
  const hasRequestedDeviceInfoRef = React.useRef(false);

  const {
    companies,
    currentCompany,
    sites,
    isAdmin,
    isCompanyManager,
    isSiteManager,
    isLoading: loading,
    loadingSites: loadingMySite,
  } = useCompanySiteScope();

  // 기기 목록 조회
  // selectedSiteId가 유효한 숫자일 때만 site_id로 API 호출
  // 'all' 또는 null인 경우: SiteManager면 null로 호출(백엔드 JWT 처리), 아니면 조회 안 함
  const effectiveSiteId =
    selectedSiteId && selectedSiteId !== 'all' ? (selectedSiteId as number) : null;

  const shouldFetchDevices = isSiteManager
    ? !loadingMySite && sites.length > 0 // 사이트가 있으면 항상 조회 (null이면 전체 조회)
    : effectiveSiteId !== null; // 특정 사이트 선택 시만 조회

  const {
    data: devices = [],
    isLoading: loadingDevices,
    refetch: refetchDevices,
  } = useGetSiteDevices(effectiveSiteId as number, {
    enabled: shouldFetchDevices,
  });

  // 기기 등록 mutation
  const registerDeviceMutation = useRegisterDevice();
  const deleteDeviceMutation = useDeleteDevice();

  // ==================== 사이트 정보 조회 ====================
  // 페이지 진입 시 현재 선택된 사이트의 정보 및 매니저 정보 조회
  const { data: siteInfo, isLoading: loadingSiteInfo } = useGetSiteInfo(selectedSiteId as number, {
    enabled: !!selectedSiteId && (selectedSiteId as number) > 0,
  });

  const currentSite = siteInfo?.site;
  const siteManagers = siteInfo?.siteManagers || [];

  // ==================== ConnectDevice 콜백 함수 ====================

  /**
   * 연결 해제 콜백
   * ConnectDevice 컴포넌트에서 연결이 해제되었을 때 호출됨
   */
  const onDisconnected = React.useCallback(() => {
    setCurDevice(null);
    setCurDeviceConfig(null);
    setMappedDevice(null);
    setExistingDeviceInfo(null);
    hasRequestedDeviceInfoRef.current = false; // 플래그 리셋
  }, []);

  /**
   * 연결 타입 변경 콜백
   * ConnectDevice 컴포넌트에서 연결 타입이 변경되었을 때 호출됨
   */
  const onConnectionChange = React.useCallback(() => {
    // 연결 타입 변경 시 필요한 로직이 있다면 여기에 추가
  }, []);

  // handleDisconnect를 위한 ref (순환 의존성 해결)
  const handleDisconnectRef = React.useRef<(() => void) | null>(null);

  const validateDeviceOwnership = React.useCallback(
    async (deviceInfo: DeviceInfo) => {
      const serialNumber = deviceInfo.serial_number as string;

      // Step 1: 현재 선택된 사이트의 기기인지 로컬 확인
      const deviceInCurrentSite = devices.find(d => d.serial_number === serialNumber);
      if (deviceInCurrentSite) {
        return;
      }

      // Step 2: 백엔드 API로 소유권 확인
      let ownership;
      try {
        ownership = await checkDeviceOwnership(serialNumber);
      } catch (error) {
        console.error('❌ [Sites] checkDeviceOwnership failed:', error);
        return;
      }

      // Case A: 미등록 기기 -> 등록 가능
      if (!ownership.registered) {
        return;
      }

      const info = ownership.ownership!;

      // Case E: 타 컴퍼니 기기
      if (!info.is_same_company) {
        showAlert(
          t(i18nKeys.site.deviceBelongsToOtherCompany),
          t(i18nKeys.site.deviceBelongsToOtherCompanyDesc),
          () => {
            handleDisconnectRef.current?.();
          }
        );
        return;
      }

      // Case D: 같은 컴퍼니이지만 내가 관리하지 않는 사이트
      if (!info.is_managed_site) {
        showConfirm(
          t(i18nKeys.site.deviceBelongsToUnmanagedSite),
          t(i18nKeys.site.deviceBelongsToUnmanagedSiteDesc),
          () => {
            handleDisconnectRef.current?.();
          },
          () => {
            handleDisconnectRef.current?.();
          }
        );
        return;
      }

      // Case C: 내가 관리하는 다른 사이트 -> 사이트 전환 제안
      if (info.site_id !== selectedSiteId) {
        showConfirm(
          t(i18nKeys.site.deviceBelongsToAnotherSite),
          t(i18nKeys.site.deviceBelongsToAnotherSiteDesc, { siteName: info.site_name }),
          () => {
            setSelectedSiteId(info.site_id);
            if (info.company_id !== selectedCompanyId) {
              setSelectedCompanyId(info.company_id);
            }
          },
          () => {
            handleDisconnectRef.current?.();
          }
        );
      }
    },
    [
      devices,
      selectedSiteId,
      selectedCompanyId,
      showConfirm,
      showAlert,
      setSelectedSiteId,
      setSelectedCompanyId,
      t,
    ]
  );

  /**
   * 기기 정보 로드 완료 콜백
   * ConnectDevice 컴포넌트에서 기기 정보를 성공적으로 가져왔을 때 호출됨
   */
  const onDeviceLoaded = React.useCallback(
    async (deviceInfo: any, deviceConfig: any) => {
      setCurDevice(deviceInfo);
      setCurDeviceConfig(deviceConfig);
      setMappedDevice(mapDeviceJson(deviceInfo));

      // 전역 상태 업데이트
      if (deviceInfo.serial_number && connectionType !== 'none') {
        updateConnectedDevice(
          deviceInfo.serial_number,
          uiConnectionTypeToLoadingProtocol(connectionType),
          {
            name: deviceInfo.serial_number,
            serialNumber: deviceInfo.serial_number,
            ...deviceInfo,
          }
        );
      }

      // 등록된 기기인지 확인
      const serialNumber = deviceInfo.serial_number as string;
      const existingDevice = devices.find(d => d.serial_number === serialNumber);

      if (existingDevice) {
        // 기존 등록된 기기 정보 저장
        setExistingDeviceInfo(existingDevice);

        // 기존 메타데이터를 mappedDevice에 적용
        setMappedDevice(prev =>
          prev
            ? {
                ...prev,
                cloud_name: existingDevice.cloud_name || prev.cloud_name,
                cloud_user: existingDevice.cloud_user || prev.cloud_user,
                cloud_desc: existingDevice.cloud_desc || prev.cloud_desc,
              }
            : null
        );

        // connectionStore cloud 정보 업데이트
        updateDeviceCloud({
          cloudName: existingDevice.cloud_name ?? '',
          cloudUser: existingDevice.cloud_user ?? '',
          cloudDesc: existingDevice.cloud_desc ?? '',
        });

        // 알림 표시
        showInfo(i18nKeys.site.deviceAlreadyRegistered, i18nKeys.site.deviceInfoUpdateDescription);
      } else {
        setExistingDeviceInfo(null);
      }

      // ===== 기기 소유권 검증 (BLE/Serial 공통) =====
      if (loadingDevices) {
        return;
      }

      await validateDeviceOwnership(deviceInfo);
    },
    [
      connectionType,
      updateConnectedDevice,
      devices,
      loadingDevices,
      showInfo,
      validateDeviceOwnership,
    ]
  );

  // ==================== useConnectionActions 훅 ====================
  const { handleDisconnect } = useConnectionActions(
    onDeviceLoaded,
    onConnectionChange,
    onDisconnected,
    false // autoRefreshOnMount: 수동으로 처리
  );

  // handleDisconnect ref 업데이트
  React.useEffect(() => {
    handleDisconnectRef.current = handleDisconnect;
  }, [handleDisconnect]);

  // 권한 확인: Admin/CompanyManager/SiteManager가 아닌 경우 접근 차단
  useEffect(() => {
    if (isAuthenticated && !isAdmin && !isCompanyManager && !isSiteManager) {
      showError(i18nKeys.site.companyManagerRequired, i18nKeys.site.companyManagerRequired);
      navigate('/');
    }
  }, [isAuthenticated, isAdmin, isCompanyManager, isSiteManager, navigate]);

  // 가이드 시작/종료에 따라 샘플 기기 행 노출 여부를 토글한다.
  useEffect(() => {
    const handleGuideStart = (event: Event) => {
      const detail = (event as CustomEvent).detail as { guideKey?: string } | undefined;
      if (detail?.guideKey === 'siteDetail') setIsGuideActive(true);
    };
    const handleGuideEnd = (event: Event) => {
      const detail = (event as CustomEvent).detail as { guideKey?: string } | undefined;
      if (detail?.guideKey === 'siteDetail') setIsGuideActive(false);
    };
    window.addEventListener('bellcloud:guide:start', handleGuideStart);
    window.addEventListener('bellcloud:guide:end', handleGuideEnd);
    return () => {
      window.removeEventListener('bellcloud:guide:start', handleGuideStart);
      window.removeEventListener('bellcloud:guide:end', handleGuideEnd);
    };
  }, []);

  // 첫 번째 회사 자동 선택 (Admin/CompanyManager: 선택된 회사가 없을 때)
  useEffect(() => {
    if (!isSiteManager && companies.length > 0 && !selectedCompanyId) {
      setSelectedCompanyId(companies[0].id);
    }
  }, [isSiteManager, companies, selectedCompanyId]);

  // SiteManager: 자신의 사이트 자동 선택 (아직 선택이 없을 때만)
  useEffect(() => {
    if (isSiteManager && !loadingMySite) {
      if (sites.length > 0 && !selectedSiteId) {
        setSelectedSiteId(sites[0].id);
      } else if (sites.length === 0 && selectedSiteId !== null) {
        setSelectedSiteId(null);
      }
    }
  }, [isSiteManager, sites, loadingMySite, selectedSiteId]);

  // Admin/CompanyManager: 첫 번째 사이트 자동 선택
  useEffect(() => {
    if (!isSiteManager && sites.length > 0 && !selectedSiteId) {
      setSelectedSiteId(sites[0].id);
    }
  }, [isSiteManager, sites, selectedSiteId]);

  // ==================== 페이지 진입 시 기기 연결 상태 확인 및 정보 요청 ====================
  /**
   * Sites 페이지 진입 시 기기가 이미 연결되어 있는 경우 자동으로 기기 정보 요청
   * - BLE: 자동으로 기기 정보 요청
   * - Serial(Cradle): 자동으로 기기 정보 요청 (Load Device 클릭 불필요)
   * (검증 로직은 onDeviceLoaded 콜백에서 처리됨)
   */
  useEffect(() => {
    // 기기가 확정되지 않았으면 스킵
    if (device.type === 'none') {
      hasRequestedDeviceInfoRef.current = false;
      return;
    }

    // 연결 타입이 없으면 스킵
    if (protocol.type === 'none') {
      return;
    }

    // 이미 요청했으면 스킵 (중복 요청 방지)
    if (hasRequestedDeviceInfoRef.current) {
      return;
    }

    // 플래그 설정
    hasRequestedDeviceInfoRef.current = true;

    // ===== BLE/Serial 연결 시 자동으로 기기 정보 요청 =====
    const fetchDeviceInfo = async () => {
      try {
        // store에 이미 deviceInfo가 있으면 onDeviceLoaded 호출 (검증 로직 실행)
        // device.type !== 'none' is already guaranteed by the outer guard
        await onDeviceLoaded(device.info as any, device.config as any);
      } catch (error) {
        console.error('❌ [Sites] Failed to fetch device info:', error);

        // BLE 초기화 에러는 조용히 처리
        const errorMessage = error instanceof Error ? error.message : String(error);
        if (errorMessage.includes('bleNotInitialized') || errorMessage.includes('Not connected')) {
          console.warn('⚠️ [Sites] Device not ready, skipping device info request');
        } else {
          showError(i18nKeys.site.deviceConfigFailed, i18nKeys.site.deviceConfigFailed);
        }

        hasRequestedDeviceInfoRef.current = false; // 실패 시 플래그 리셋
      } finally {
        hideLoading();
      }
    };

    fetchDeviceInfo();
  }, [device.type, protocol.type, onDeviceLoaded, hideLoading, showError]);

  /**
   * 기기 등록 (크래들/블루투스 공통)
   *
   * 과정:
   * 1. 기기 정보 및 사이트 ID 검증
   * 2. API를 통해 기기 등록
   * 3. 기기 목록 새로고침
   * 4. 상태 초기화
   */
  const registerDevice = async () => {
    try {
      setRegistering(true);
      if (!curDevice || !selectedSiteId) {
        showError(i18nKeys.site.deviceInfoMissing, i18nKeys.site.deviceInfoMissing);
        return;
      }

      showLoading(t(i18nKeys.site.deviceRegistering));

      const serialNumber = curDevice.serial_number as string;

      // ✅ 리팩토링: devices 테이블은 메타데이터만 저장
      // 실제 디바이스 정보는 deviceInfo/deviceConfig로 전달
      const result = await registerDeviceMutation.mutateAsync({
        serial_number: serialNumber,
        site_id: selectedSiteId as number,
        device_type: mappedDevice?.device_type,
        cloud_name: mappedDevice?.cloud_name,
        cloud_user: mappedDevice?.cloud_user,
        cloud_desc: mappedDevice?.cloud_desc,
        deviceInfo: curDevice as unknown as Record<string, unknown>,
        deviceConfig: curDeviceConfig as unknown as Record<string, unknown>,
      });

      await refetchDevices();

      // 이미 등록된 기기인 경우와 새로 등록된 경우 메시지 구분
      if (result.already_registered) {
        // 복합 메시지는 키값으로 처리하기 어려우므로 기본 메시지 사용
        showSuccess(i18nKeys.site.deviceInfoUpdated);
      } else {
        showSuccess(i18nKeys.site.deviceRegistered);
      }

      setCurDevice(null);
      setCurDeviceConfig(null);
      setMappedDevice(null);
      setExistingDeviceInfo(null);
    } catch (error: unknown) {
      console.error('❌ [registerDevice] 에러:', error);

      const e = parseSiteApiError(error);

      if (e.message === 'device:max_devices_exceeded') {
        showAlert(
          t(i18nKeys.device.max_devices_exceeded_title),
          t(i18nKeys.device.max_devices_exceeded_desc),
          undefined,
          'danger'
        );
      } else {
        showError(e.title, e.message);
      }

      // 에러 발생 시에도 상태 초기화
      setCurDevice(null);
      setMappedDevice(null);
      setExistingDeviceInfo(null);
    } finally {
      setRegistering(false);
      hideLoading();
    }
  };

  // ==================== 기기 편집/삭제 로직 ====================

  /**
   * 기기 편집 모달 열기
   */
  const handleEditDevice = (device: DeviceTableRow) => {
    // 가이드용 샘플 기기는 실제 모달을 열지 않는다.
    if (device.id < 0) return;
    setEditingDevice(device);
  };

  /**
   * 기기 삭제
   */
  const handleDeleteDevice = (device: DeviceTableRow) => {
    // 가이드용 샘플 기기는 삭제 확인을 띄우지 않는다.
    if (device.id < 0) return;
    const name = (device.cloud_name?.trim() || device.serial_number) ?? '';
    showConfirm(
      t(i18nKeys.site.delete),
      t(i18nKeys.site.confirmDeleteDevice, { name }),
      () => {
        void (async () => {
          try {
            showLoading(t(i18nKeys.site.deviceDeleting));
            await deleteDeviceMutation.mutateAsync(device.id);
            await refetchDevices();
            showSuccess(i18nKeys.site.deviceDeleted);
          } catch (error: unknown) {
            console.error('❌ [handleDeleteDevice] 에러:', error);
            const e = parseSiteApiError(error);
            showError(i18nKeys.site.deviceDeleteFailed, e.message);
          } finally {
            hideLoading();
          }
        })();
      },
      undefined,
      'danger'
    );
  };

  const selectedSite = sites.find(s => s.id === selectedSiteId);

  // 가이드 진행 중, 등록된 기기가 없을 때 수정/삭제/상세 step 의 타겟을 보장하기 위한 샘플 기기.
  const guideSampleDevice = {
    id: -1,
    serial_number: t(i18nKeys.guide.sample_device),
    cloud_name: null,
    device_type: 'mini',
    sensor_types: [],
    is_connected: false,
    calibration_due_date: null,
    cal_due_day_num: null,
    calibration_interval: null,
    calibration_interval_array: null,
    bump_test_due_date: null,
    bump_due_day_num: null,
    last_connect: null,
  } as unknown as DeviceTableRow;

  // 테이블에 표시할 기기 목록 (기기가 없고 가이드 중이면 샘플 한 건을 노출)
  const devicesToRender: DeviceTableRow[] =
    devices.length > 0
      ? (devices as unknown as DeviceTableRow[])
      : isGuideActive
        ? [guideSampleDevice]
        : [];

  const handleExcelExportWithDetails = useCallback(() => {
    if (devices.length === 0) return;

    const rows = (devices as unknown as DeviceTableRow[]).map(d => {
      const calibInterval =
        d.calibration_interval_array ??
        (d.calibration_interval != null ? [d.calibration_interval] : undefined);
      const bumpInterval: number[] | undefined =
        (d as any).bump_interval_array ??
        (Array.isArray((d as any).bump_interval)
          ? (d as any).bump_interval
          : (d as any).bump_interval != null
            ? [(d as any).bump_interval]
            : undefined);

      return {
        'Serial Number': d.serial_number,
        'Device Type': getDeviceDisplayName(d.device_type || '', d.app_id),
        'Cloud Name': d.cloud_name ?? '',
        Sensors: (d.sensor_types || []).join(', '),
        'Calibration Due': formatDueDateLine(
          d.calibration_due_date,
          d.cal_due_day_num,
          calibInterval
        ),
        'Cal Due Days': d.cal_due_day_num ?? '',
        'Bump Test Due': formatDueDateLine(d.bump_test_due_date, d.bump_due_day_num, bumpInterval),
        'Bump Due Days': d.bump_due_day_num ?? '',
        Connected: d.is_connected ? 'Online' : 'Offline',
        'Last Connect': d.last_connect ? formatDate(d.last_connect) : '',
        'Cloud User': (d as any).cloud_user ?? '',
        'Cloud Description': (d as any).cloud_desc ?? '',
        'HW Version': (d as any).hw_version ?? '',
        'FW Version': (d as any).fw_version ?? '',
        'Manufacturer Date': (d as any).manufacturer_date
          ? formatDate((d as any).manufacturer_date)
          : '',
        'Fixed Latitude': (d as any).fixed_latitude ?? '',
        'Fixed Longitude': (d as any).fixed_longitude ?? '',
      };
    });

    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Devices');

    const dateStr = new Date().toISOString().split('T')[0];
    XLSX.writeFile(wb, `devices_${selectedSite?.name || 'site'}_${dateStr}.xlsx`);
  }, [devices, selectedSite]);

  return (
    <>
      <DefaultContents>
        {loading ? (
          <Flex align="center" justify="center" style={{ padding: theme.spacing[8] }}>
            <Flex align="center" gap={2}>
              <Spinner size="md" />
              <Text color="textSecondary">{t(i18nKeys.site.loading)}</Text>
            </Flex>
          </Flex>
        ) : (
          <>
            {selectedSite && (
              <>
                {/* 통계 카드 */}
                {(() => {
                  // device.list + Dashboard static과 동일: CalibrationDueDateService 기준 D-day
                  const overdueCount = devices.filter(d => {
                    const n = d.cal_due_day_num;
                    return n != null && n < 0;
                  }).length;

                  const dueIn30DaysCount = devices.filter(d => {
                    const n = d.cal_due_day_num;
                    return n != null && n >= 0 && n <= 30;
                  }).length;

                  const bumpOverdueCount = devices.filter(d => {
                    const n = d.bump_due_day_num;
                    return n != null && n < 0;
                  }).length;

                  const bumpDueIn30DaysCount = devices.filter(d => {
                    const n = d.bump_due_day_num;
                    return n != null && n >= 0 && n <= 30;
                  }).length;

                  return (
                    <div data-tour="site-summary">
                      <SummaryCard
                        items={[
                          {
                            label: t(i18nKeys.site.totalDevices),
                            value: devices.length,
                            unit: null,
                            decimal: null,
                            state: null,
                          },
                          {
                            label: 'Calibration Overdue',
                            value: overdueCount,
                            unit: null,
                            decimal: null,
                            state: 'error',
                          },
                          {
                            label: 'Calibration Due in 30 Days',
                            value: dueIn30DaysCount,
                            unit: null,
                            decimal: null,
                            state: 'warning',
                          },
                          {
                            label: 'Bump Test Overdue',
                            value: bumpOverdueCount,
                            unit: null,
                            decimal: null,
                            state: 'error',
                          },
                          {
                            label: 'Bump Test Due in 30 Days',
                            value: bumpDueIn30DaysCount,
                            unit: null,
                            decimal: null,
                            state: 'warning',
                          },
                        ]}
                      />
                    </div>
                  );
                })()}
                {/* 사이트 정보 및 매니저 정보 카드 */}
                <Grid columns={2} gap={4} responsive>
                  {/* LEFT: Site 정보 */}
                  <Card height="auto" data-tour="site-info">
                    <Flex justify="between" align="center">
                      <CardTitle>Site Information</CardTitle>
                      {(isAdmin || isCompanyManager || isSiteManager) &&
                        (currentSite || selectedSite) && (
                          <IconButton
                            data-tour="site-info-edit"
                            onClick={() => setShowSiteEditModal(true)}
                            aria-label="Edit site"
                          >
                            <img src="/icons/edit.svg" alt="" width="30" height="30" />
                          </IconButton>
                        )}
                    </Flex>

                    <div>
                      {loadingSiteInfo ? (
                        <Flex align="center" justify="center" style={{ padding: theme.spacing[4] }}>
                          <Spinner size="sm" />
                          <Text color="textSecondary" style={{ marginLeft: theme.spacing[2] }}>
                            {t(i18nKeys.site.loading)}
                          </Text>
                        </Flex>
                      ) : (
                        <Grid columns={2} gap={4} responsive>
                          <InfoRow label="Site Name" value={(currentSite || selectedSite)?.name} />
                          <InfoRow
                            label="Location"
                            value={(currentSite || selectedSite)?.location || ''}
                          />
                          <InfoRow label="Description" value={currentSite?.description || ''} />
                          <InfoRow
                            label="Telephone"
                            value={formatPhoneForDisplay(
                              (currentSite || selectedSite)?.telephone || ''
                            )}
                          />
                        </Grid>
                      )}
                    </div>
                  </Card>

                  {/* RIGHT: Site Manager 정보 */}
                  <Card height="auto" data-tour="site-manager-info">
                    {(() => {
                      const manager = siteManagers[0] ?? null;
                      return (
                        <>
                          <Flex justify="between" align="center">
                            <CardTitle>Site Manager</CardTitle>
                            {manager && (isAdmin || isCompanyManager) && (
                              <IconButton
                                data-tour="site-manager-info-edit"
                                onClick={() => {
                                  setEditingSiteManager({
                                    id: manager.id,
                                    site_id: selectedSiteId as number,
                                    email: manager.email,
                                    name: manager.name,
                                    phone: manager.phone,
                                    role_id: 3,
                                    company_id: currentCompany?.id || currentSite?.company_id || 0,
                                    is_used: true,
                                    sites: [],
                                    created_at: manager.created_at,
                                  });
                                  setShowSiteManagerEditModal(true);
                                }}
                                aria-label="Edit site manager"
                              >
                                <img src="/icons/edit.svg" alt="" width="30" height="30" />
                              </IconButton>
                            )}
                            {manager && isSiteManager && manager.id === currentUser?.id && (
                              <IconButton
                                data-tour="site-manager-info-edit"
                                onClick={() => setShowProfileModal(true)}
                                aria-label="Edit profile"
                              >
                                <img src="/icons/edit.svg" alt="" width="30" height="30" />
                              </IconButton>
                            )}
                          </Flex>

                          {loadingSiteInfo ? (
                            <Flex
                              align="center"
                              justify="center"
                              style={{ padding: theme.spacing[4] }}
                            >
                              <Spinner size="sm" />
                            </Flex>
                          ) : manager ? (
                            <Grid columns={2} gap={4} responsive>
                              <InfoRow label="Name" value={manager.name || ''} />
                              <InfoRow label="Phone" value={formatPhoneForDisplay(manager.phone)} />
                              <InfoRow label="Email" value={manager.email || ''} />
                            </Grid>
                          ) : (
                            <Text size="sm" color="textSecondary">
                              No site manager assigned
                            </Text>
                          )}
                        </>
                      );
                    })()}
                  </Card>
                </Grid>
                <Flex
                  direction={isMobile ? 'column' : 'row'}
                  align="stretch"
                  gap={4}
                  style={{
                    flex: isMobile ? '0 0 auto' : 1,
                    minHeight: 0,
                    maxHeight: isMobile ? 'none' : 'calc(100vh - 100px)',
                    overflow: isMobile ? 'visible' : 'hidden',
                  }}
                >
                  {/* ===== 기기 등록 섹션 ===== */}
                  <Flex
                    direction="column"
                    gap={4}
                    style={{
                      flex: isMobile ? '0 0 auto' : '0 0 450px',
                      width: isMobile ? '100%' : undefined,
                      overflow: isMobile ? 'visible' : 'auto',
                    }}
                  >
                    {/* Register Device Card - 내용에 맞게 크기 조절 */}
                    <Card fullWidth fullHeight data-tour="device-register">
                      <CardTitle>{t(i18nKeys.site.registerDevice)}</CardTitle>

                      {/* ConnectDevice 컴포넌트 - 상세 정보 포함 */}
                      <ConnectDevice
                        onDeviceLoaded={onDeviceLoaded}
                        onConnectionChange={onConnectionChange}
                        onDisconnected={onDisconnected}
                        deviceImage={false}
                        showDetailInfo={true}
                      />

                      {/* Register Device 버튼 */}
                      <Button
                        onClick={registerDevice}
                        disabled={!curDevice || !selectedSiteId || registering}
                        width="100%"
                        variant="secondary"
                      >
                        {registering
                          ? t(i18nKeys.site.registering)
                          : existingDeviceInfo
                            ? t(i18nKeys.site.deviceInfoUpdate)
                            : t(i18nKeys.site.registerDevice)}
                      </Button>
                    </Card>
                  </Flex>

                  {/* Registered Devices Table - 남은 공간 모두 차지, 내부 스크롤 */}

                  <div
                    data-tour="registered-device-list"
                    style={{
                      flex: isMobile ? '0 0 auto' : 1,
                      width: isMobile ? '100%' : undefined,
                      minHeight: 0,
                      height: isMobile ? '600px' : undefined,
                      display: 'flex',
                      flexDirection: 'column',
                      padding: theme.spacing[6],
                      borderRadius: theme.borderRadius.lg,
                      border: `1px solid ${theme.colors.border.divider}`,
                      backgroundColor: theme.colors.surface.layout.container,
                      boxShadow: theme.shadows.md,
                      overflow: 'hidden',
                    }}
                  >
                    <Flex
                      justify="between"
                      align="center"
                      style={{ flexShrink: 0, marginBottom: theme.spacing[4] }}
                    >
                      <CardTitle style={{ margin: 0 }}>
                        {t(i18nKeys.site.registeredDevices)}
                      </CardTitle>
                      <Button
                        variant="primary"
                        data-tour="device-download-excel"
                        onClick={handleExcelExportWithDetails}
                        disabled={devices.length === 0}
                        style={{ width: '178px' }}
                      >
                        Download Excel
                      </Button>
                    </Flex>
                    <div
                      style={{
                        flex: 1,
                        minHeight: 0,
                        overflow: 'auto',
                      }}
                    >
                      <DataTable<DeviceTableRow>
                        data={devicesToRender}
                        columns={createDeviceTableColumns({
                          t,
                          theme,
                          onEdit: handleEditDevice,
                          onDelete: handleDeleteDevice,
                        })}
                        isLoading={loadingDevices}
                        emptyMessage={t(i18nKeys.site.noRegisteredDevices)}
                        enableSorting={true}
                        enableFiltering={true}
                        enableExpanding={true}
                        getRowCanExpand={() => true}
                        renderSubComponent={row => <DeviceExpandedRow device={row.original} />}
                        tableStyle={{ minWidth: '1100px' }}
                      />
                    </div>
                  </div>
                </Flex>
              </>
            )}
          </>
        )}

        {/* 기기 편집 모달 */}
        <DeviceEditModal
          device={editingDevice}
          isOpen={!!editingDevice}
          onClose={() => setEditingDevice(null)}
          onSuccess={refetchDevices}
        />

        {/* 사이트 편집 모달 */}
        {showSiteEditModal && (currentCompany?.id || currentSite?.company_id) && (
          <SiteCrudModal
            isOpen={showSiteEditModal}
            site={currentSite ?? null}
            companyId={currentCompany?.id || currentSite?.company_id || 0}
            onClose={() => setShowSiteEditModal(false)}
            onSave={() => setShowSiteEditModal(false)}
          />
        )}

        {/* Site Manager 편집 모달 (Admin / CompanyManager) */}
        {showSiteManagerEditModal && editingSiteManager && (
          <SiteManagerCrudModal
            isOpen={showSiteManagerEditModal}
            siteManager={editingSiteManager}
            companyId={editingSiteManager.company_id}
            onClose={() => {
              setShowSiteManagerEditModal(false);
              setEditingSiteManager(null);
            }}
            onSave={() => {
              setShowSiteManagerEditModal(false);
              setEditingSiteManager(null);
            }}
          />
        )}

        {/* 본인 프로필 편집 모달 (SiteManager) */}
        {showProfileModal && currentUser && (
          <CompanyManagerEditModal
            isOpen={showProfileModal}
            companyManager={{
              id: currentUser.id,
              name: currentUser.name || '',
              email: currentUser.email || '',
              phone: currentUser.phone || null,
            }}
            onClose={() => setShowProfileModal(false)}
            onSave={() => setShowProfileModal(false)}
          />
        )}
      </DefaultContents>
    </>
  );
};

export default Sites;
